import React from 'react';

/**
 * The Recruiter24x7 logo, exactly as the website draws it: a white hexagon on
 * a rounded square filled with the brand gradient, beside a wordmark whose
 * "24x7" is painted in that same gradient.
 *
 * One component for every surface — the sidebar, the sign-in page, the
 * candidate portal and the interview rooms — so the app and the website never
 * drift apart again.
 */

const GRADIENT_FROM = '#640d5f';
const GRADIENT_TO = '#d91656';

// Each mark carries its own gradient definition. A shared id would break the
// moment a page rendered two marks, or none at the root.
let seq = 0;

export function BrandMark({ size = 30 }) {
  const id = React.useMemo(() => `brand-gradient-${seq += 1}`, []);
  return (
    <svg width={size} height={size} viewBox="0 0 30 30" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={GRADIENT_FROM} />
          <stop offset="100%" stopColor={GRADIENT_TO} />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="28" height="28" rx="8" fill={`url(#${id})`} />
      <svg x="21%" y="21%" width="58%" height="58%" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2.8 20 7.4v9.2l-8 4.6-8-4.6V7.4z"
          stroke="#fff"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    </svg>
  );
}

/** "Recruiter24x7", with 24x7 in the brand gradient. */
export function BrandWordmark({ size = 16, color, weight = 700, style }) {
  return (
    <span style={{ fontSize: size, fontWeight: weight, color: color || 'var(--text-heading)', letterSpacing: '-0.3px', whiteSpace: 'nowrap', ...style }}>
      Recruiter
      <span style={{
        background: `linear-gradient(135deg, ${GRADIENT_FROM}, ${GRADIENT_TO})`,
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
      }}>
        24x7
      </span>
    </span>
  );
}

/** The mark and the wordmark together, as the website's header shows them. */
export default function BrandLogo({ size = 30, textSize = 18, color, gap = 8, style }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap, ...style }}>
      <BrandMark size={size} />
      <BrandWordmark size={textSize} color={color} />
    </span>
  );
}
